import { Button, EmptyState, Input, Table, useConfirm, useToast } from '@/components';
import { ApiClientError, type UserAppleUdid } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { UdidStatusBadge } from './UdidStatusBadge';
import { useAddAppleUdid, useDeleteAppleUdid, useMyAppleUdids, useUpdateAppleUdid } from './hooks';

// Personal settings card: your Apple device UDIDs for iOS ad hoc builds. A device only
// receives ad hoc builds after an admin dispatches registration (Release >
// Apple UDID) and the next build runs — hence the Pending/Registered badge.
export const MyUdidsSection = () => {
  const udids = useMyAppleUdids();
  const add = useAddAppleUdid();
  const update = useUpdateAppleUdid();
  const remove = useDeleteAppleUdid();
  const toast = useToast();
  const confirmDialog = useConfirm();

  const [udid, setUdid] = createSignal('');
  const [label, setLabel] = createSignal('');
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [editLabel, setEditLabel] = createSignal('');
  const [helpOpen, setHelpOpen] = createSignal(false);

  const onAdd = async (e: SubmitEvent) => {
    e.preventDefault();
    try {
      await add.mutateAsync({ udid: udid(), label: label() });
      setUdid('');
      setLabel('');
      toast.success('Device added');
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.error.message);
      else toast.error('Failed to add device');
    }
  };

  const onSaveLabel = async (row: UserAppleUdid) => {
    try {
      await update.mutateAsync({ udidId: row.id, label: editLabel() });
      setEditingId(null);
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.error.message);
      else toast.error('Failed to rename device');
    }
  };

  const onDelete = async (row: UserAppleUdid) => {
    const ok = await confirmDialog({
      title: `Remove "${row.label}"?`,
      body: 'The device stays registered with Apple; it just disappears from this list.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(row.id);
      toast.success('Device removed');
    } catch {
      toast.error('Failed to remove device');
    }
  };

  return (
    <div class="flex flex-col gap-4">
      <p class="text-sm text-neutral-600 dark:text-neutral-400">
        Add your iPhone/iPad UDIDs so internal (ad hoc) iOS builds can install on them. New devices
        show as Pending until an admin registers them with Apple.
      </p>

      <Table
        data={udids.data?.data ?? []}
        rowKey={(r) => r.id}
        loading={udids.isLoading}
        empty={<EmptyState title="No devices yet" body="Add your first device below." />}
        columns={[
          {
            key: 'device',
            header: 'Device',
            render: (row: UserAppleUdid) => (
              <Show
                when={editingId() === row.id}
                fallback={<span class="font-medium">{row.label}</span>}
              >
                <form
                  class="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void onSaveLabel(row);
                  }}
                >
                  <Input
                    value={editLabel()}
                    onInput={(e) => setEditLabel(e.currentTarget.value)}
                    maxlength={50}
                    required
                    autofocus
                  />
                  <Button type="submit" size="sm" loading={update.isPending}>
                    Save
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </Button>
                </form>
              </Show>
            ),
          },
          {
            key: 'udid',
            header: 'UDID',
            render: (row: UserAppleUdid) => <span class="font-mono text-xs">{row.udid}</span>,
          },
          {
            key: 'registered',
            header: 'Registered',
            render: (row: UserAppleUdid) => <UdidStatusBadge row={row} />,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            render: (row: UserAppleUdid) => (
              <div class="flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditLabel(row.label);
                    setEditingId(row.id);
                  }}
                >
                  Rename
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void onDelete(row)}>
                  Remove
                </Button>
              </div>
            ),
          },
        ]}
      />

      <form class="flex flex-wrap items-end gap-3" onSubmit={(e) => void onAdd(e)}>
        <div class="min-w-64 flex-1">
          <Input
            label="UDID"
            value={udid()}
            onInput={(e) => setUdid(e.currentTarget.value)}
            placeholder="00008030-000A1B2C3D4E5F60"
            required
          />
        </div>
        <div class="min-w-40">
          <Input
            label="Device name"
            value={label()}
            onInput={(e) => setLabel(e.currentTarget.value)}
            placeholder="My iPhone 15"
            maxlength={50}
            required
          />
        </div>
        <Button type="submit" loading={add.isPending}>
          Add device
        </Button>
      </form>

      <div>
        <button
          type="button"
          class="text-brand-600 text-sm hover:underline dark:text-brand-400"
          aria-expanded={helpOpen()}
          onClick={() => setHelpOpen(!helpOpen())}
        >
          How do I find my UDID?
        </button>
        <div
          class={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
            helpOpen() ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div class="overflow-hidden">
            <ol class="mt-2 list-decimal space-y-1 pl-5 text-neutral-600 text-sm dark:text-neutral-400">
              <li>
                <strong>On a Mac:</strong> connect the device with a cable, open Finder, select the
                device in the sidebar, then click the line under the device name until it shows the
                UDID. Right-click it to copy.
              </li>
              <li>
                <strong>On the device:</strong> Settings ▸ General ▸ About shows the serial — the
                UDID itself is only visible via Finder/iTunes or a profile tool, so use a Mac when
                possible.
              </li>
              <li>Paste it here exactly; letter case does not matter.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};
